import { Alert, Box, Button, Drawer, Group, Select, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { useEffect, useState } from "react";
import type { FeePolicyCustomer } from "../../apis/tenantFeePolicyAssignment";
import type { FeePolicy } from "../../apis/tenantFeePolicy";

interface Props {
  opened: boolean;
  customer: FeePolicyCustomer | null;
  namedPolicies: FeePolicy[];
  /** Apply a NAMED policy to this customer. */
  onAssign: (params: { feePolicyId: string; effectiveFrom: string }) => Promise<void>;
  /** Revert this customer to DEFAULT (close the active assignment). */
  onRevert: () => Promise<void>;
  onClose: () => void;
}

const REVERT_VALUE = "__REVERT__";

// Single-customer assignment editor (spec §3.3 "Per-row edit"). Lets an admin pick a
// NAMED policy for one customer, or revert them to DEFAULT (soft-removes the assignment).
export function AssignCustomerDrawer({ opened, customer, namedPolicies, onAssign, onRevert, onClose }: Props) {
  const [policyValue, setPolicyValue] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState<Date | null>(new Date());
  const [busy, setBusy] = useState(false);

  // Customer is on a NAMED policy when an assignment id is present.
  const hasNamed = !!customer?.assignmentId;

  // Reset the form whenever a different row opens the drawer.
  useEffect(() => {
    if (opened) {
      setPolicyValue(customer?.effectivePolicy?.scope === "NAMED" ? customer.effectivePolicy.id : null);
      setEffectiveFrom(new Date());
    }
  }, [opened, customer]);

  const data = [
    ...namedPolicies.map((p) => ({
      value: p.id,
      label: `${p.name ?? "(нэргүй)"}${
        p.calculationMethod === "PERCENTAGE" && p.feeRate ? ` (${(Number(p.feeRate) * 100).toFixed(2)}%)` : ""
      }`,
    })),
    // Revert is only meaningful when the customer currently has a NAMED assignment.
    ...(hasNamed ? [{ value: REVERT_VALUE, label: "↩ DEFAULT-руу буцаах" }] : []),
  ];

  // No assignable NAMED policy exists. A customer already on NAMED can still revert to DEFAULT,
  // so the Select stays usable in that case; otherwise there is nothing to pick.
  const noNamedAvailable = namedPolicies.length === 0;
  const selectDisabled = noNamedAvailable && !hasNamed;

  const canApply = policyValue !== null && !busy && (policyValue === REVERT_VALUE || effectiveFrom !== null);

  const handleApply = async () => {
    if (!policyValue) return;
    setBusy(true);
    try {
      if (policyValue === REVERT_VALUE) {
        await onRevert();
      } else if (effectiveFrom) {
        await onAssign({ feePolicyId: policyValue, effectiveFrom: effectiveFrom.toISOString().slice(0, 10) });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={onClose} title="Харилцагчид policy оноох" position="right" size="md">
      <Stack gap="md">
        {customer && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">Харилцагч</Text>
            <Text fw={600}>{customer.name ?? customer.businessId}</Text>
            {customer.effectivePolicy && (
              <Text size="xs" c="dimmed">
                Одоогийн бодлого: {customer.effectivePolicy.scope === "DEFAULT" ? "DEFAULT" : customer.effectivePolicy.name ?? "(нэргүй)"}
              </Text>
            )}
          </Stack>
        )}

        {noNamedAvailable && (
          <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
            Идэвхтэй нэртэй (NAMED) бодлого алга байна. Харилцагчид оноохын тулд эхлээд нэртэй бодлого үүсгэж идэвхжүүлнэ үү.
          </Alert>
        )}

        <Select
          label="Бодлого"
          placeholder={selectDisabled ? "Оноох боломжтой бодлого алга" : "Policy сонгох"}
          data={data}
          value={policyValue}
          onChange={setPolicyValue}
          searchable
          disabled={selectDisabled}
          nothingFoundMessage="Идэвхтэй нэртэй бодлого олдсонгүй"
        />

        {policyValue !== REVERT_VALUE && (
          <DateInput
            label="Эхлэх огноо"
            value={effectiveFrom}
            onChange={(v: unknown) =>
              setEffectiveFrom(typeof v === "string" ? new Date(v) : (v as Date | null))
            }
            valueFormat="YYYY-MM-DD"
          />
        )}

        {policyValue === REVERT_VALUE && (
          <Box>
            <Text size="sm" c="dimmed">
              Энэ харилцагчийн идэвхтэй оноолтыг хааж, DEFAULT бодлогод буцаана.
            </Text>
          </Box>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={busy}>Болих</Button>
          <Button onClick={handleApply} loading={busy} disabled={!canApply}>Хадгалах</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
